'use client';

import React, { useMemo, useState, useEffect, useRef } from "react";
import { Plus, Download, ChevronDown, BellRing, CalendarClock } from "lucide-react";
import { useJigyasa, filterCallsDue, filterAppointmentsUpcoming, filterNewToday, filterByStage, ownerMetrics } from "./state";
import { SectionTitle, PrimaryButton, SecondaryButton, Card, LineSwitcher } from "./parts";
import { EnquiryRow, KanbanCard } from "./rows";
import { EnquiryDetailSheet, AddEnquirySheet } from "./sheets";
import { SmartSuggestionsSection } from "./intelligence";
import { TrendLineChart } from "./charts";
import { exportEnquiriesCsv } from "./exportCsv";

// Round-trip 14 (13 Jun 2026) redesign:
//   - Sticky compact header (Today + actions) so the operator never
//     loses the New Enquiry button.
//   - Call-back-today as a horizontal strip pinned just below the
//     header — never hidden, never pushes the pipeline down.
//   - Pipeline becomes the main view, immediately visible on load.
//     Each column is fixed-height with INTERNAL vertical scroll, so
//     the Lost column's 34 cards don't make the whole page enormous.
//   - "New today", monthly trend, and Smart Suggestions move into a
//     collapsible "Today's overview" panel beneath the pipeline —
//     useful but secondary.
//   - Page itself essentially does not scroll vertically. The
//     operator scans left-to-right across stages.
//
// Per the idiot-proof design principle: one obvious next step (New
// enquiry, top-right), most-actionable info up top (Call back), the
// rest collapsed.
export const OperatorDesktop = ({ autoOpenAdd } = {}) => {
  const { enquiries, lineEnquiries, costume, stages, me, companyInfo } = useJigyasa();
  const [selectedId, setSelectedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);

  // Host can ask us to pop the "New enquiry" form open by changing `autoOpenAdd`
  // (e.g. the onboarding checklist's "Add one now"). A ref guards against the
  // value firing on first mount / remount — only a genuine change opens it.
  const lastAutoOpen = useRef(autoOpenAdd);
  useEffect(() => {
    if (autoOpenAdd !== lastAutoOpen.current) {
      lastAutoOpen.current = autoOpenAdd;
      setAddOpen(true);
    }
  }, [autoOpenAdd]);

  const callsDue = useMemo(() => filterCallsDue(lineEnquiries), [lineEnquiries]);
  const appts = useMemo(() => filterAppointmentsUpcoming(lineEnquiries), [lineEnquiries]);
  const newToday = useMemo(() => filterNewToday(lineEnquiries), [lineEnquiries]);
  const trend = useMemo(() => ownerMetrics(lineEnquiries).bars, [lineEnquiries]);
  const selected = enquiries.find((e) => e.id === selectedId) || null;

  // First card on the board — the Ashraya tours spotlight this one when they
  // need to say "tap a lead to open it".
  const firstCardId = useMemo(() => {
    for (const stage of stages) {
      const cards = filterByStage(lineEnquiries, stage.key);
      if (cards.length) return cards[0].id;
    }
    return null;
  }, [lineEnquiries, stages]);

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-3">
      {/* Compact sticky header — always within reach */}
      <div
        data-testid="desktop-sticky-header"
        className="sticky top-0 z-20 -mx-4 mb-3 border-b border-zinc-200 bg-zinc-50/95 px-4 py-2 backdrop-blur"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold tracking-tight text-zinc-900">
              {costume.copy.operatorHomeTitle || "Today"}
            </h1>
            <p className="truncate text-xs text-zinc-500">
              {me} · {companyInfo?.name}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <SecondaryButton
              data-testid="export-enquiries-btn"
              onClick={() => exportEnquiriesCsv(lineEnquiries, costume, stages)}
              className="gap-2"
            >
              <Download size={16} /> Export
            </SecondaryButton>
            <PrimaryButton
              data-testid="add-enquiry-button-desktop"
              data-tour-id="jigyasa-new-enquiry-btn"
              onClick={() => setAddOpen(true)}
              className="gap-2"
            >
              <Plus size={16} /> New enquiry
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Lines of business switcher (hidden when the org has just one) */}
      <LineSwitcher />

      {/* Today's overview — compact band above the pipeline.
          Default-collapsed so the leads are visible on load, but still
          discoverable. Opens IN PLACE without pushing the pipeline
          out of view. */}
      <section data-testid="desktop-overview-section" className="mb-3">
        <button
          data-testid="desktop-overview-toggle"
          onClick={() => setOverviewOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2 text-left hover:bg-zinc-50"
        >
          <span className="text-sm text-zinc-700">
            <span className="font-medium">Today&apos;s overview</span>
            <span className="text-zinc-500">
              {" · "}{newToday.length} new today
              {" · "}6-month trend
              {" · "}smart suggestions
            </span>
          </span>
          <ChevronDown
            size={16}
            className={`shrink-0 text-zinc-500 transition-transform ${overviewOpen ? "rotate-180" : ""}`}
          />
        </button>

        {overviewOpen && (
          <div className="mt-3 space-y-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <SectionTitle testId="desktop-section-new">New today</SectionTitle>
                {newToday.length === 0 ? (
                  <p className="rounded-xl border border-zinc-200 bg-white p-3 text-sm text-zinc-500">
                    Nothing fresh yet today.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {newToday.map((e) => (
                      <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} />
                    ))}
                  </div>
                )}
              </div>

              <div data-testid="desktop-trend-section">
                <SectionTitle testId="desktop-section-trend">{costume.copy.sectionTrend}</SectionTitle>
                <Card>
                  <TrendLineChart data={trend} />
                </Card>
              </div>
            </div>

            <SmartSuggestionsSection onOpenEnquiry={(id) => setSelectedId(id)} />
          </div>
        )}
      </section>

      {/* Calls to make — today + overdue follow-ups. Renders ONLY when it has
          calls, never as an empty half because demos exist. */}
      {callsDue.length > 0 && (
        <section
          data-testid="due-calls-panel"
          data-tour-id="jigyasa-due-today"
          className="mb-3 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2">
            <BellRing size={16} className="text-rose-600" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-700">
              Calls to make
            </h2>
            <span
              data-testid="due-calls-count"
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1.5 text-[11px] font-semibold text-white"
            >
              {callsDue.length}
            </span>
          </div>
          <div className="space-y-2">
            {callsDue.map((e) => (
              <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} showOverdue />
            ))}
          </div>
        </section>
      )}

      {/* Demos & visits — overdue (red, on top) + today + next 7 days, so
          client appointments can be planned ahead. Renders ONLY when it has
          appointments. */}
      {appts.length > 0 && (
        <section
          data-testid="due-appts-panel"
          className="mb-3 rounded-2xl border border-sky-200 bg-white p-3 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2">
            <CalendarClock size={16} className="text-sky-600" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-700">
              Demos &amp; visits
            </h2>
            <span
              data-testid="due-appts-count"
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sky-600 px-1.5 text-[11px] font-semibold text-white"
            >
              {appts.length}
            </span>
            <span className="text-[11px] text-zinc-500">Today + next 7 days</span>
          </div>
          <div className="space-y-2">
            {appts.map((e) => (
              <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} showOverdue />
            ))}
          </div>
        </section>
      )}

      {/* Pipeline — primary view. Fixed-height columns, internal scroll. */}
      <section data-testid="desktop-section-pipeline" data-tour-id="jigyasa-pipeline">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
            Pipeline
          </h2>
          <span className="text-[11px] text-zinc-500">
            Scroll inside a column to see more · {lineEnquiries.length} total
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7">
          {stages.map((stage, ci) => {
            const cards = filterByStage(lineEnquiries, stage.key);
            const washes = [
              "bg-emerald-50/70", "bg-rose-50/70", "bg-sky-50/70",
              "bg-amber-50/70", "bg-purple-50/70", "bg-teal-50/70", "bg-zinc-100/70",
            ];
            return (
              <div
                key={stage.key}
                data-testid={`kanban-col-${stage.key}`}
                className={`flex min-h-[320px] flex-col rounded-2xl ${washes[ci % washes.length]} p-2`}
                style={{ maxHeight: "calc(100vh - 260px)" }}
              >
                <div className="mb-2 flex shrink-0 items-center justify-between px-1 pt-0.5">
                  <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-zinc-700">
                    {stage.label}
                  </span>
                  <span
                    data-testid={`kanban-col-${stage.key}-count`}
                    className="ml-1 inline-flex h-5 min-w-[22px] shrink-0 items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-medium text-zinc-700"
                  >
                    {cards.length}
                  </span>
                </div>
                <div
                  data-testid={`kanban-col-${stage.key}-scroll`}
                  className="flex-1 space-y-2 overflow-y-auto pr-0.5"
                >
                  {cards.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-zinc-400">—</p>
                  ) : (
                    cards.map((e) => (
                      <KanbanCard
                        key={e.id}
                        enquiry={e}
                        onClick={() => setSelectedId(e.id)}
                        tourId={e.id === firstCardId ? "jigyasa-lead-card" : undefined}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <EnquiryDetailSheet enquiry={selected} onClose={() => setSelectedId(null)} />
      <AddEnquirySheet open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
};
